/**
 * The stock type ("Who can use it" before BLU-1443) as one Select value:
 * `own`, `all`, or `client:<org id>`. The operator words are Own stock,
 * Shared and Labelled for <client>; the stored audience values are unchanged.
 * Pure: no React.
 */
import { BASE_GARMENT_PRODUCT_CODE_MAX, suggestLabelPrefix, type BaseGarmentAudience } from '@threadheads/shared';
import type { TFunction } from 'i18next';

export const AUDIENCE_OWN = 'own';
export const AUDIENCE_ALL = 'all';
const CLIENT_PREFIX = 'client:';

export interface AudienceChoice {
  audience: BaseGarmentAudience;
  /** Set exactly when `audience` is `client`. */
  clientOrganizationId?: string;
}

export function audienceValue(choice: { audience: BaseGarmentAudience; clientOrganizationId?: string | null }): string {
  if (choice.audience === 'client' && choice.clientOrganizationId) return `${CLIENT_PREFIX}${choice.clientOrganizationId}`;
  return choice.audience === 'all_clients' ? AUDIENCE_ALL : AUDIENCE_OWN;
}

/**
 * BLU-1436: one SKU's stock type as a Select value, the way clients really see it:
 * not shared = `own`; shared and labelled for a client = `client:<id>`; shared = `all`.
 */
export function skuAudienceValue(isShared: boolean, reservedForOrganizationId: string | null | undefined): string {
  if (!isShared) return AUDIENCE_OWN;
  return reservedForOrganizationId ? `${CLIENT_PREFIX}${reservedForOrganizationId}` : AUDIENCE_ALL;
}

export function parseAudienceValue(value: string): AudienceChoice {
  if (value.startsWith(CLIENT_PREFIX)) {
    return { audience: 'client', clientOrganizationId: value.slice(CLIENT_PREFIX.length) };
  }
  return { audience: value === AUDIENCE_ALL ? 'all_clients' : 'own' };
}

/**
 * The words for one stock type: "Own stock", "Shared", "Labelled for Mini Zali".
 * A client whose name is unknown (or empty) reads "Labelled for a client".
 */
export function audienceLabel(
  t: TFunction<'inventory'>,
  audience: BaseGarmentAudience,
  clientName: string | null | undefined,
): string {
  if (audience === 'all_clients') return t('baseGarments.who.allClients');
  if (audience === 'client') {
    return clientName ? t('baseGarments.who.onlyClient', { client: clientName }) : t('baseGarments.who.reservedFor');
  }
  return t('baseGarments.who.onlyUs');
}

export interface AudienceOption {
  value: string;
  label: string;
  /** A longer line under the label in the list. */
  hint?: string;
}

/**
 * The stock types a Select offers. `clients` is the connected roster; `exclude`
 * drops one value (Make a copy leaves out the code's own stock type, R13/R30);
 * `stockedOnly` keeps Own stock and Shared (a code with stock can't be
 * labelled for a client, F7).
 */
export function audienceOptions(
  t: TFunction<'inventory'>,
  clients: ReadonlyArray<{ id: string; name: string }>,
  { exclude, stockedOnly = false }: { exclude?: string; stockedOnly?: boolean } = {},
): AudienceOption[] {
  const options: AudienceOption[] = [
    { value: AUDIENCE_OWN, label: t('baseGarments.who.onlyUs'), hint: t('baseGarments.who.onlyUsHint') },
    { value: AUDIENCE_ALL, label: t('baseGarments.who.allClients'), hint: t('baseGarments.who.allClientsHint') },
  ];
  if (!stockedOnly) {
    for (const client of clients) {
      options.push({
        value: `${CLIENT_PREFIX}${client.id}`,
        label: audienceLabel(t, 'client', client.name),
      });
    }
  }
  return exclude === undefined ? options : options.filter((option) => option.value !== exclude);
}

// ── Make a copy ────────────────────────────────────────────────────────────

/** What a copy's code is worked out from. */
export interface CopySource {
  code: string;
  audience: BaseGarmentAudience;
  clientName: string | null;
}

/** The code with the reserved client's label prefix taken off (`THTEE` for Threadheads is `TEE`). */
export function baseOfCode(row: CopySource): string {
  if (row.audience !== 'client' || !row.clientName) return row.code;
  const prefix = suggestLabelPrefix(row.clientName);
  return prefix !== '' && row.code.startsWith(prefix) && row.code.length > prefix.length
    ? row.code.slice(prefix.length)
    : row.code;
}

/** The copy's suggested code: the target client's prefix plus the base code, at most 10 characters. */
export function suggestCopyCode(row: CopySource, targetClientName: string | null): string {
  const prefix = targetClientName ? suggestLabelPrefix(targetClientName) : '';
  return `${prefix}${baseOfCode(row)}`.slice(0, BASE_GARMENT_PRODUCT_CODE_MAX);
}
